import { useScrollAnimation } from '@/hooks/useScrollAnimation';


const services = [
  {
    icon: 'ri-palette-line',
    title: 'Diseño Gráfico',
    description: 'Creación de piezas visuales que comunican la esencia de tu marca con impacto y coherencia.',
    items: ['Tarjetas de presentación', 'Volantes y afiches', 'Branding e identidad', 'Papelería corporativa'],
    color: '#FF6B35',
    bg: 'rgba(255,107,53,0.12)',
  },
  {
    icon: 'ri-smartphone-line',
    title: 'Contenido Digital',
    description: 'Diseño de contenido visual estratégico para redes sociales que genera engagement y conversión.',
    items: ['Posts para Instagram', 'Stories y Reels', 'Banners digitales', 'Piezas para Facebook'],
    color: '#00D9FF',
    bg: 'rgba(0,217,255,0.12)',
  },
  {
    icon: 'ri-video-line',
    title: 'Edición de Video',
    description: 'Producción y edición de videos dinámicos para redes sociales, publicidad y presentaciones.',
    items: ['Reels y TikToks', 'Videos corporativos', 'Animaciones básicas', 'Intros y closings'],
    color: '#FF006E',
    bg: 'rgba(255,0,110,0.12)',
  },
  {
    icon: 'ri-box-3-line',
    title: 'Diseño de Empaques',
    description: 'Diseño de empaques y cajas que destacan en el punto de venta y refuerzan la identidad de marca.',
    items: ['Cajas de producto', 'Etiquetas y stickers', 'Empaques premium', 'Diseño para impresión'],
    color: '#FFD60A',
    bg: 'rgba(255,214,10,0.12)',
  },
  {
    icon: 'ri-printer-line',
    title: 'Publicidad Impresa',
    description: 'Diseño y producción de material publicitario impreso con acabados de alta calidad.',
    items: ['Pendones y vallas', 'Catálogos y brochures', 'Serigrafía', 'Material POP'],
    color: '#06FFA5',
    bg: 'rgba(6,255,165,0.12)',
  },
  {
    icon: 'ri-brush-line',
    title: 'Serigrafía',
    description: 'Impresión serigráfica de alta calidad con formación técnica del SENA, ideal para textiles, papelería y material promocional.',
    items: ['Camisetas y textiles', 'Bolsas y empaques', 'Papelería corporativa', 'Material promocional'],
    color: '#FF6B35',
    bg: 'rgba(255,107,53,0.12)',
  },
  {
    icon: 'ri-code-s-slash-line',
    title: 'Páginas Web',
    description: 'Diseño y desarrollo de sitios web modernos, rápidos y con identidad visual sólida para marcas que quieren destacar en digital.',
    items: ['Landing pages', 'Sitios corporativos', 'Portafolios digitales', 'Sitios para emprendedores'],
    color: '#00D9FF',
    bg: 'rgba(0,217,255,0.12)',
  },
];

export default function Services() {
  const ref = useScrollAnimation();

  return (
    <section id="services" className="py-24 md:py-32 relative bg-black overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#FF6B35]/3 blur-3xl pointer-events-none"></div>

      <div ref={ref} className="relative z-[3] max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="fade-in-up flex items-center gap-4 mb-6">
          <span className="text-[#FF6B35] font-bebas text-lg tracking-widest">03</span>
          <div className="w-12 h-px bg-[#FF6B35]"></div>
          <span className="text-white/40 text-sm tracking-widest uppercase">Servicios</span>
        </div>

        <div className="fade-in-up mb-16">
          <h2 className="font-bebas text-5xl md:text-6xl lg:text-7xl text-white leading-tight">
            MIS <span className="gradient-text">SERVICIOS</span>
          </h2>
          <p className="text-white/50 font-poppins text-base mt-4 max-w-xl">
            Soluciones visuales completas para marcas que quieren destacar y comunicar con impacto.
          </p>
        </div>

        {/* Services grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, i) => (
            <div
              key={service.title}
              id={`service-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
              className="fade-in-up group relative bg-[#0d0d0d] border border-white/5 rounded-2xl p-8 card-hover cursor-default overflow-hidden"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {/* Hover accent top border */}
              <div
                className="absolute top-0 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-500 rounded-t-2xl"
                style={{ backgroundColor: service.color }}
              ></div>

              {/* Icon */}
              <div
                className="w-16 h-16 flex items-center justify-center rounded-2xl mb-6"
                style={{ backgroundColor: service.bg }}
              >
                <i className={`${service.icon} text-2xl`} style={{ color: service.color }}></i>
              </div>

              {/* Title */}
              <h3 className="font-poppins font-bold text-xl text-white mb-3">{service.title}</h3>

              {/* Description */}
              <p className="text-white/50 text-sm leading-relaxed mb-6">{service.description}</p>

              {/* Items */}
              <ul className="space-y-2">
                {service.items.map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-white/60">
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: service.color }}></span>
                    {item}
                  </li>
                ))}
              </ul>

              {/* Bottom glow */}
              <div
                className="absolute bottom-0 right-0 w-32 h-32 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-2xl pointer-events-none"
                style={{ backgroundColor: service.bg }}
              ></div>
            </div>
          ))}

          {/* CTA card */}
          <div className="fade-in-up bg-gradient-to-br from-[#FF6B35]/20 to-[#FF006E]/20 border border-[#FF6B35]/20 rounded-2xl p-8 flex flex-col items-center justify-center text-center card-hover cursor-pointer group">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF006E] mb-6 group-hover:scale-110 transition-transform duration-300">
              <i className="ri-chat-1-line text-2xl text-white"></i>
            </div>
            <h3 className="font-poppins font-bold text-xl text-white mb-3">¿Tienes un proyecto?</h3>
            <p className="text-white/60 text-sm mb-6">Cuéntame tu idea y creamos algo increíble juntos.</p>
            <a
              href="https://wa.me/573024819377?text=Hola%20Michael%2C%20tengo%20un%20proyecto%20para%20ti"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-black font-semibold text-sm px-6 py-3 rounded-full hover:bg-[#FF6B35] hover:text-white transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-whatsapp-line"></i>
              Hablemos ahora
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
